/**
 * STEP 2 — Size & Paper (iteration-2 "3-pane" rework, round-5 polish).
 *
 * Desktop ≥1024px: three vertical panes — PHOTOS (select, ~47%) · CONFIGURE
 * (size, paper, qty, border/frame, crop → Add, ~32%) · ORDER (photo-centric
 * live cart, ~21%). At 768–1024px the Order pane collapses into a slide-over
 * drawer. <768px switches to sticky SEGMENTED TABS (Photos · Configure ·
 * Cart(N)).
 *
 * Round-5 changes: the Easy Print Wizard re-offers on EVERY mount while ≥40
 * photos are ready and any photo has no prints (and its apply REPLACES the
 * whole order); the sticky bottom bar gains the Add to cart action (mirrors
 * Configure) and owns the only Continue; Continue is never disabled by
 * fix-needed states — it shows a red ! badge and opens the review dialog.
 */

import { seedPricing } from '@rpl/pricing';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatMoney } from '../../lib/format';
import {
  continueGateSummary,
  flaggedForReview,
  isBlockedPrint,
  needsBorderGate,
  wizardAutoPrompt,
} from '../../lib/sizePaper';
import { useCartExtras } from '../../store/cartExtras';
import { useStore, useTotals } from '../../store/store';
import { Chrome } from '../chrome/Chrome';
import { SaveOrderModal } from '../cart/SaveOrderModal';
import { reportUpsell } from '../../lib/upsellReport';
import { AddFrameDialog } from '../cart/AddFrameDialog';
import { groupMembers } from '../cart/cartModel';
import { sameSizeFrameOption } from '../cart/frame/frameOptions';
import { smallPrintsOffer } from '../../lib/upsell';
import { sizeShort } from '../../lib/sizePaper';
import { Segmented } from '../sizepaper/Segmented';
import { CropEditor } from '../crop/CropEditor';
import { GuideModal } from '../guide/GuideModal';
import { BorderGateModal } from '../sizepaper/BorderGateModal';
import { CartPane } from '../sizepaper/CartPane';
import { ConfigurePane, type AddMeta, type AddResult } from '../sizepaper/ConfigurePane';
import { EasyWizardModal } from '../sizepaper/EasyWizardModal';
import { FrameNudgeSheet } from '../sizepaper/FrameNudgeSheet';
import { mobileFooterCta } from '../sizepaper/mobileCta';
import { MobileTabs, type SpTab } from '../sizepaper/MobileTabs';
import { ModalShell } from '../sizepaper/ModalShell';
import { PaperInfoDialog } from '../sizepaper/PaperInfoDialog';
import { PhotosPane } from '../sizepaper/PhotosPane';
import { ReviewCropsModal } from '../sizepaper/ReviewCropsModal';
import { useOrderActions } from '../sizepaper/useOrderActions';
import '../sizepaper/sizepaper.css';

export function SizePaperScreen() {
  const isMobile = useIsMobile();
  const photos = useStore((s) => s.photos);
  const prints = useStore((s) => s.prints);
  const pricing = useStore((s) => s.pricing) ?? seedPricing;
  const applyBar = useStore((s) => s.applyBar);
  const guideSeen = useStore((s) => s.guideSeen);
  const setApplyBar = useStore((s) => s.setApplyBar);
  const setStep = useStore((s) => s.setStep);
  const markGuideSeen = useStore((s) => s.markGuideSeen);

  const totals = useTotals();
  const actions = useOrderActions();
  const { catalog } = actions;

  const [cropId, setCropId] = useState<string | null>(null);
  /* The upsell beacon's project handle is armed at module scope in
     store/store.ts (audit B2) — it used to be armed by an effect HERE, and the
     cleanup nulled it on unmount, killing every beacon fired from the Cart. */
  /* Post-Add frame nudge (upsell #1): the print we just asked about, and —
     after "See it framed" — the print whose group AddFrameDialog opens for. */
  const [frameNudge, setFrameNudge] = useState<{ printId: string; photoId: string } | null>(null);
  const [frameDialogFor, setFrameDialogFor] = useState<string | null>(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Round 7: wizard handoff — when set, the Review dialog is SCOPED to these
  // freshly-created prints (null = the normal full flagged list).
  const [reviewScope, setReviewScope] = useState<string[] | null>(null);
  const [attnOpen, setAttnOpen] = useState(false); // unresolved-attention confirm
  const [discardOpen, setDiscardOpen] = useState(false); // "are you sure" on discard
  const [wizardOpen, setWizardOpen] = useState(false);
  // Save-for-later on Step 2 (owner 2026-07-29): a second chance at the email
  // for anyone who skipped the post-upload prompt. Reuses the existing modal.
  const [saveOpen, setSaveOpen] = useState(false);
  const savedEmail = useCartExtras((s) => s.savedEmail);
  const hydrateSavedEmail = useCartExtras((s) => s.hydrateSavedEmail);
  const paperInfoOpen = useStore((s) => s.paperInfoOpen); // store-held: survives layout remounts
  const spPendingFrameId = useStore((s) => s.spPendingFrameId);
  const setPaperInfoOpen = useStore((s) => s.setPaperInfoOpen);
  // Store-held (owner 2026-07-26): the browser back-gesture sync
  // (lib/backNav) drives these, and they survive layout remounts.
  const tab: SpTab = useStore((s) => s.spTab);
  const setTab = useStore((s) => s.setSpTab);
  const cartOpen = useStore((s) => s.spCartOpen); // medium slide-over
  const setCartOpen = useStore((s) => s.setSpCartOpen);
  // Print Check chip filter removed (owner 2026-07-27) — the Add toast and
  // the Continue review dialogs carry the warnings instead.
  const [toast, setToast] = useState<{ msg: string; key: number } | null>(null);
  const [pulseKey, setPulseKey] = useState(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The Configure pane registers its Add action so the sticky bar can mirror it.
  const [addMeta, setAddMeta] = useState<AddMeta | null>(null);
  const addRef = useRef<(() => void) | null>(null);

  const readyCount = photos.filter((p) => p.status === 'ready').length;

  const pushToast = (msg: string) => {
    setToast({ msg, key: Date.now() });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };
  useEffect(() => () => void (toastTimer.current && clearTimeout(toastTimer.current)), []);

  // Learn the project's stored email once, so a resumed saved order shows
  // "Saved" here instead of asking for an address it already has.
  useEffect(() => {
    void hydrateSavedEmail();
  }, [hydrateSavedEmail]);

  /* ---------- defaults on arrival ---------- */
  useEffect(() => {
    if (!applyBar.sizeId) {
      const fallback = catalog.has('ct-4x6') ? 'ct-4x6' : ([...catalog.keys()][0] ?? null);
      if (fallback) setApplyBar({ sizeId: fallback });
    }
  }, [applyBar.sizeId, catalog, setApplyBar]);

  /* ---------- large-batch wizard auto-offer (round 5: EVERY mount while ≥40
     ready photos and any photo still has no prints — newly-added photos since
     the wizard last ran re-trigger it; the persistent chip stays either way). */
  const wizardChecked = useRef(false);
  useEffect(() => {
    if (wizardChecked.current || isMobile || readyCount === 0) return;
    wizardChecked.current = true; // evaluate once per mount, when photos are in
    const readyIds = photos.filter((p) => p.status === 'ready').map((p) => p.id);
    if (wizardAutoPrompt(readyIds, prints)) setWizardOpen(true);
  }, [readyCount, photos, prints, isMobile]);

  // Round 3: Step 2 mounts with NOTHING selected — the customer picks photos
  // deliberately (the Easy Print Wizard applies to all ready photos itself).

  const ready = readyCount > 0;
  const pricedCount = totals?.printCount ?? 0;
  const subtotal = totals?.subtotal ?? 0;

  /** READY photos currently selected — drives the mobile guided footer + cue.
   * Returns 0 on desktop so selection changes never re-render this screen
   * there (desktop stays exactly as before; the panes subscribe themselves). */
  const selectedReadyCount = useStore((s) =>
    isMobile
      ? s.photos.reduce((n, p) => (p.status === 'ready' && s.selection.has(p.id) ? n + 1 : n), 0)
      : 0,
  );

  /* Mobile one-shot cue: when the FIRST photo gets selected on the Photos tab
     (a 0 → ≥1 transition observed while on mobile), pulse the Configure pill
     once so the eye lands on the next step. */
  const [configureCue, setConfigureCue] = useState(0);
  const prevSelected = useRef<number | null>(null);
  useEffect(() => {
    if (!isMobile) {
      prevSelected.current = null; // re-observe from scratch if we flip to mobile
      return;
    }
    const prev = prevSelected.current;
    prevSelected.current = selectedReadyCount;
    if (configureCue > 0) return; // one-shot per mount
    if (prev === 0 && selectedReadyCount > 0 && tab === 'photos') setConfigureCue(1);
  }, [isMobile, tab, selectedReadyCount, configureCue]);

  /* Single-photo fast path (owner 2026-08-17, mobile): after uploading ONE
     photo, landing on the Photos tab just makes them re-select the photo they
     obviously came to print. Auto-select it and open Customise directly.
     Once per mount, one-photo orders only — a second upload ends the shortcut,
     and coming BACK to the Photos tab is never hijacked. */
  const autoConfigured = useRef(false);
  useEffect(() => {
    if (!isMobile || autoConfigured.current || tab !== 'photos') return;
    if (photos.length !== 1 || photos[0]!.status !== 'ready') return;
    autoConfigured.current = true;
    const s = useStore.getState();
    s.setSelection([photos[0]!.id]);
    s.setSpCfgStep('size');
    s.setSpTab('configure');
  }, [isMobile, tab, photos]);

  /** Unresolved attention items (auto-crops on the silent default / blocked low-res). */
  const attention = continueGateSummary(prints);

  // Round 5: fix-needed states never disable Continue — the button shows a red
  // ! badge and opens the review dialog. Only "nothing in the order" disables.
  const continueDisabled = pricedCount === 0 && !attention;

  /** After the attention gate clears: fine-art border gate, then Step 3.
   *  Reads the store, not this render's `prints`: callers change the order
   *  in the same tap (discard, add-all) and a stale list would gate on prints
   *  that are already gone. */
  const continuePastAttention = () => {
    if (needsBorderGate(useStore.getState().prints)) setGateOpen(true);
    else setStep(2);
  };

  /** "Discard & continue": REMOVE the prints the review lists (auto-crops on
   *  the silent default + blocked low-res), then carry on. It used to only
   *  continue, so the auto-cropped prints were still charged and the low-res
   *  ones, hidden from the cart, still blocked checkout. */
  /** Frames are the dearest thing Discard can remove: the confirm names them. */
  const discardFramedCount = flaggedForReview(prints).filter((p) => p.frameId).length;
  const discardFlaggedAndContinue = () => {
    const { prints: now, removePrint } = useStore.getState();
    for (const p of flaggedForReview(now)) removePrint(p.id);
    continuePastAttention();
  };

  /* Round 8: photos with NO prints silently fall out of the order — warn once
     at Continue so nobody discovers missing prints at pickup. */
  const printlessCount = photos.reduce(
    (n, p) =>
      n + (p.status === 'ready' && !prints.some((pr) => pr.photoId === p.id) ? 1 : 0),
    0,
  );
  const [printlessOpen, setPrintlessOpen] = useState(false);
  /* No-prints dialog OFFER variant (owner 2026-08-17): the warning about
     photos being left out IS the natural upsell moment, so when the left-out
     photos ratio-fit a small size (quality-gated, budget-capped 25%-of-cart /
     $12) the primary action becomes "Add all N · +$X" instead of homework.
     The cart-exit sheet handles only DUPLICATES, so no photo is asked about
     twice. */
  const noprintsShownRef = useRef(false);
  // Owner 2026-08-17: quick finish toggle on the offer, LUSTRE default.
  const [printlessPaper, setPrintlessPaper] = useState<'gloss' | 'lustre'>('lustre');
  const printlessOffer = useMemo(
    () =>
      printlessOpen
        ? smallPrintsOffer(
            pricing,
            catalog,
            photos,
            prints,
            actions.thresholds,
            totals?.subtotal ?? 0,
            'unprinted',
          )
        : null,
    [printlessOpen, pricing, catalog, photos, prints, actions.thresholds, totals?.subtotal],
  );
  useEffect(() => {
    if (!printlessOpen || !printlessOffer || noprintsShownRef.current) return;
    noprintsShownRef.current = true;
    reportUpsell('shown', 'noprints_offer', printlessOffer.items[0]!.sizeId);
  }, [printlessOpen, printlessOffer]);

  /* AUDIT B4 (2026-08-26): only "Continue without them" beaconed an outcome.
     "Go back" and the modal close (Escape, backdrop click) emitted nothing,
     so ~93% of this surface's exits were invisible and its shown→outcome
     accounting never balanced. `backed_out` is deliberately a THIRD action,
     not another `dismissed`: leaving to go pick sizes yourself is not the same
     answer as declining the offer and continuing anyway, and merging them
     would flatter the dismissal rate. */
  const backedOutRef = useRef(false);
  const backOutPrintless = () => {
    /* Backing out is the ONE exit that leaves the customer here, still able to
       re-trigger the dialog at the next Continue — and `shown` is emitted once
       per mount, so an ungated re-fire would push backed_out above shown for
       the surface. Gate it to match. (Accepted stays ungated on purpose: it is
       the revenue-bearing event the Worker resolves against the final cart, so
       a duplicate is far cheaper than a miss.) */
    if (printlessOffer && noprintsShownRef.current && !backedOutRef.current) {
      backedOutRef.current = true;
      reportUpsell('backed_out', 'noprints_offer', printlessOffer.items[0]!.sizeId);
    }
    setPrintlessOpen(false);
  };

  const tryContinue = () => {
    if (attention) setAttnOpen(true);
    else if (printlessCount > 0) setPrintlessOpen(true);
    else continuePastAttention();
  };

  /** Open the Review-crops dialog: full list, or scoped to specific prints. */
  const openReview = (scope: string[] | null = null) => {
    setReviewScope(scope);
    setReviewOpen(true);
  };

  const onAdded = (r: AddResult) => {
    const noun = r.framed ? 'framed print' : 'print';
    const base = `Added ${r.count} ${r.count === 1 ? noun : `${noun}s`} to your order ✓`;
    pushToast(r.flagged > 0 ? `${base} · ${r.flagged} to review` : base);
    setPulseKey((k) => k + 1);

    /* Frame nudge (#1): the "fries with that?" moment. One ask per order,
       single-photo adds only, same-size frame must exist (never a 4×6), and
       an order that already holds a framed print has answered the question. */
    if (r.framed || !r.singlePrintId) return;
    const st = useStore.getState();
    if (st.frameNudgeSeen) return;
    if (st.prints.some((p) => p.frameId)) return;
    const added = st.prints.find((p) => p.id === r.singlePrintId);
    // A blocked low-res print isn't priced yet, so it has no cart group and
    // AddFrameDialog can't open for it: "See it framed" did nothing (owner
    // 2026-10-12, an A2 "needs review"). Don't ask, and don't spend the
    // once-per-order nudge, until a print that can actually be framed is added.
    if (!added || !added.paper || isBlockedPrint(added)) return;
    const offer = sameSizeFrameOption(pricing, added.sizeId, added.paper);
    if (!offer) return;
    st.setFrameNudgeSeen(true);
    reportUpsell('shown', 'frame_sheet', offer.frameId);
    setFrameNudge({ printId: added.id, photoId: added.photoId });
  };

  /** Editor SAVE feedback (owner 2026-07-26): editing a print used to close
   *  silently — confirm it landed, and pulse the cart since price/contents
   *  may have changed (e.g. a frame added in Adjustments). */
  const onEdited = (r: { framed: boolean }) => {
    pushToast(r.framed ? 'Changes saved · framed print in your order ✓' : 'Changes saved ✓');
    setPulseKey((k) => k + 1);
  };

  /* ---------- Esc: close the drawer / collapse to Photos ---------- */
  useEffect(() => {
    if (cropId || gateOpen || reviewOpen || wizardOpen || attnOpen || paperInfoOpen)
      return; // modals own Escape
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (cartOpen) setCartOpen(false);
      else if (isMobile && tab !== 'photos') setTab('photos');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cropId, gateOpen, reviewOpen, wizardOpen, attnOpen, paperInfoOpen, cartOpen, isMobile, tab]);

  const cropPrint = cropId ? prints.find((p) => p.id === cropId) : undefined;
  const cropPhoto = cropPrint ? photos.find((p) => p.id === cropPrint.photoId) : undefined;

  /* Owner 2026-07-27: "order this photo" intents (lightbox Order print, cart
     + add size) no longer open the add-a-print dialog — they land the
     customer in the Customise pane ready to pick size/paper. Review round:
     these are explicit SINGLE-photo intents, so REPLACE the selection (a
     lingering multi-select would make the next Add print unrelated photos,
     and the preview now opens on the named photo); close the tablet cart
     slide-over (it covered the pane — the click looked dead); the tab flip
     is mobile-only (desktop always shows the pane, and a tab write there
     pushed a phantom back-gesture history entry). */
  const orderIntoConfigure = (photoId: string) => {
    const s = useStore.getState();
    s.setSelection([photoId]);
    if (s.spCartOpen) s.setSpCartOpen(false);
    // A new photo's flow must not inherit the last one's pending frame — it
    // would open already framed, with the seeded size replaced by the frame's
    // locked read-out (review round 2026-07-27).
    s.setSpPendingFrameId(null);
    if (isMobile) {
      s.setSpCfgStep('size'); // an explicit "order this photo" starts the flow over
      s.setSpTab('configure');
    }
  };

  /** Photo-pane CHIP tap (owner 2026-07-27): also into Customise — seeded
   *  with that print's size/paper — instead of jumping straight into the
   *  crop dialog (the cart pane's ✎ keeps the direct editor). */
  const chipIntoConfigure = (printId: string) => {
    const s = useStore.getState();
    const p = s.prints.find((pr) => pr.id === printId);
    if (!p) return;
    s.setApplyBar({ sizeId: p.sizeId, paper: p.paper });
    orderIntoConfigure(p.photoId);
  };

  /** The step-level Continue — desktop bar + the mobile Cart tab share it. */
  const continueAction = {
    label: 'Continue →',
    onClick: tryContinue,
    disabled: continueDisabled,
    alert: !!attention,
  };

  /* Mobile guided next-step (mobileCta.ts): the sticky footer's primary walks
     the customer Photos → Configure → Cart, then hands over to the real
     Continue — whose attention dialog + fine-art border gate keep firing from
     the Cart tab. Desktop always gets continueAction. */
  const mobileCta =
    isMobile && ready
      ? mobileFooterCta({
          tab,
          selectedCount: selectedReadyCount,
          cartCount: pricedCount,
          hasPrints: prints.length > 0,
        })
      : ({ kind: 'continue' } as const);
  /* Owner 2026-07-27: on the CUSTOMISE tab the bar's one button IS the stepped
     flow's action ("Next →" / gold "Add to cart · N prints") — that's the 67px
     that lets the steps fit without scrolling. The Cart pill (with its badge)
     is still one tap away, so nothing becomes unreachable. */
  const mobilePrimary =
    // selectedReadyCount > 0 mirrors the pane's own `stepped` condition — with
    // nothing selected the pane is a placeholder, so fall back to the normal
    // CTA rather than showing a dead disabled "Add to cart".
    isMobile && ready && tab === 'configure' && addMeta && selectedReadyCount > 0
      ? {
          label: addMeta.label,
          onClick: () => addRef.current?.(),
          disabled: !addMeta.canAdd,
          gold: addMeta.gold,
        }
      : mobileCta.kind === 'continue'
        ? continueAction
        : {
            label: mobileCta.label,
            onClick: () => setTab(mobileCta.kind === 'to-configure' ? 'configure' : 'cart'),
          };

  /* Adaptive Photos pane (owner 2026-07-27 v2): desktop width follows the
     photo count — 1-column rail <10 · 2-column rail 10-40 · full 40+. A
     chevron press overrides; pressing it back RETURNS TO THE AUTO TIER (the
     origin you expanded/collapsed from), and the override is sticky until
     then. */
  const spPhotosMode = useStore((s) => s.spPhotosMode);
  const setSpPhotosMode = useStore((s) => s.setSpPhotosMode);
  const readyPhotoCount = photos.reduce((n, p) => n + (p.status === 'ready' ? 1 : 0), 0);
  const railTier: 'rail' | 'rail2' = readyPhotoCount < 10 ? 'rail' : 'rail2';
  const autoMode: 'rail' | 'rail2' | 'full' = readyPhotoCount > 40 ? 'full' : railTier;
  const paneMode: 'rail' | 'rail2' | 'full' =
    spPhotosMode === 'auto' ? autoMode : spPhotosMode === 'full' ? 'full' : railTier;
  const togglePane = () => {
    if (spPhotosMode !== 'auto') setSpPhotosMode('auto'); // back to the origin tier
    else setSpPhotosMode(paneMode === 'full' ? 'rail' : 'full');
  };

  const photosPane = (
    <PhotosPane
      pricing={pricing}
      catalog={catalog}
      actions={actions}
      onOpenCrop={chipIntoConfigure}
      onAddPrint={orderIntoConfigure}
      onOpenWizard={() => setWizardOpen(true)}
      onReview={() => openReview()}
      onToast={pushToast}
      paneMode={isMobile ? 'full' : paneMode}
      /* Mobile pins paneMode to 'full', so the « collapse control toggled the
         store and changed nothing on screen (owner 2026-07-27). PhotosPane
         already hides the button when this is undefined. */
      onTogglePane={isMobile ? undefined : togglePane}
    />
  );
  /* Save-for-later on Step 2 (owner 2026-07-29). Deliberately NOT in the
     Customise pane: that step's vertical budget is measured in single pixels
     and anything added there costs scrolling. On mobile it rides the foot of
     the PHOTOS tab, flex:none under a flex:1 pane, so the grid keeps its space
     and the Customise step is untouched. Desktop uses the header slot instead
     (see headerRight), which costs no layout at all. */
  const photosPaneMobile = (
    <>
      {photosPane}
      <div className="sp3__saverow">
        {savedEmail ? (
          <span className="sp3__saved">✓ Saved to {savedEmail}</span>
        ) : (
          <button type="button" className="linkbtn linkbtn--dark" onClick={() => setSaveOpen(true)}>
            Save for later
          </button>
        )}
      </div>
    </>
  );
  const configurePane = (
    <ConfigurePane
      pricing={pricing}
      catalog={catalog}
      actions={actions}
      onOpenCrop={setCropId}
      onAdded={onAdded}
      onAddMeta={setAddMeta}
      addRef={addRef}
      onToast={pushToast}
    />
  );
  const cartPane = (
    <CartPane
      pricing={pricing}
      onOpenCrop={setCropId}
      onAddPrint={orderIntoConfigure}
      onReview={() => openReview()}
      pulseKey={pulseKey}
    />
  );

  let body: React.ReactNode;
  if (!ready) {
    body = (
      <div className="pane__empty sp3__empty">
        <p>No photos ready yet.</p>
        <button type="button" className="btn btn--secondary" onClick={() => setStep(0)}>
          ← Back to upload
        </button>
      </div>
    );
  } else if (isMobile) {
    body = (
      <MobileTabs
        tab={tab}
        onTab={setTab}
        cartCount={pricedCount}
        configureCue={configureCue}
        photos={photosPaneMobile}
        configure={configurePane}
        cart={cartPane}
      />
    );
  } else {
    body = (
      <div
        className={`sp3${cartOpen ? ' sp3--cart-open' : ''}${
          paneMode === 'rail' ? ' sp3--rail' : paneMode === 'rail2' ? ' sp3--rail2' : ''
        }`}
      >
        {photosPane}
        {configurePane}
        <button
          type="button"
          className="sp3__cart-toggle"
          aria-expanded={cartOpen}
          onClick={() => setCartOpen(!cartOpen)}
        >
          Order ({pricedCount}) · {formatMoney(subtotal)} ▸
        </button>
        <div className="sp3__scrim" onMouseDown={() => setCartOpen(false)} aria-hidden={!cartOpen} />
        <div className="sp3__cartwrap">{cartPane}</div>
      </div>
    );
  }

  return (
    <Chrome
      step={1}
      headerRight={
        /* Save-for-later lives beside the running total: the one header slot
           that already exists on Step 2, so it costs no layout and crowds
           nothing. Desktop only — Chrome does not render this on mobile, which
           is why the Photos tab carries its own row. */
        <span className="sp3__hdr">
          <span>
            Order so far: <strong>{formatMoney(subtotal)}</strong>
          </span>
          {savedEmail ? (
            <span className="sp3__saved" title={`Saved to ${savedEmail}`}>
              ✓ Saved
            </span>
          ) : (
            <button
              type="button"
              className="linkbtn linkbtn--dark"
              onClick={() => setSaveOpen(true)}
            >
              Save for later
            </button>
          )}
        </span>
      }
      /* Owner 2026-07-27: once the order has prints, "Cart" in the stepper is
         a real destination, not a greyed-out future step. It routes through
         tryContinue, NOT a raw setStep, so the review gate and the fine-art
         border gate still fire exactly as they do on Continue. */
      stepperForward={prints.length > 0 ? [2] : undefined}
      onStepperForward={tryContinue}
      footer={{
        // Status text + the glanceable "ⓘ Paper Details" chip (round 11) so the
        // paper reference is one tap away at any point on Step 2.
        status: (
          <>
            {(() => {
              // Goal-gradient framing (behavioral round): progress toward
              // "every photo has a print" motivates completing the set.
              const readyCount = photos.reduce((n, p) => n + (p.status === 'ready' ? 1 : 0), 0);
              const withPrints = Math.max(0, readyCount - printlessCount);
              if (pricedCount === 0) return 'Pick photos, choose a size, then add to cart';
              const printsPart = `${pricedCount} print${pricedCount === 1 ? '' : 's'} in your order`;
              if (printlessCount > 0)
                return `${printsPart} · ${withPrints} of ${readyCount} photos done`;
              return `${printsPart} · all ${readyCount} photos done ✓`;
            })()}
            <button type="button" className="paper-chip" onClick={() => setPaperInfoOpen(true)}>
              ⓘ Paper Details
            </button>
          </>
        ),
        secondary: { label: '← Back', onClick: () => setStep(0) },
        // (The old `accent` Add mirror lived here. It was unreachable — this
        // `footer` prop only renders on DESKTOP, where the pane has its own
        // pinned Add row. Mobile now drives `mobileFooter.primary` instead.)
        primary: continueAction,
      }}
      mobileFooter={{
        totalLabel: 'Order',
        totalValue: formatMoney(subtotal),
        primary: mobilePrimary,
      }}
    >
      {body}

      {toast && (
        <div key={toast.key} className="sp-toast" role="status" aria-live="polite">
          {toast.msg}
        </div>
      )}

      {wizardOpen && (
        <EasyWizardModal
          pricing={pricing}
          catalog={catalog}
          actions={actions}
          photoCount={readyCount}
          onApply={(ids) => {
            setWizardOpen(false);
            if (ids.length > 0) {
              pushToast(`Set up ${ids.length} photo${ids.length === 1 ? '' : 's'} ✓`);
              setStep(2);
            }
          }}
          onReview={(flaggedIds) => openReview(flaggedIds)}
          onDismiss={() => setWizardOpen(false)}
        />
      )}

      {reviewOpen && (
        <ReviewCropsModal
          actions={actions}
          scopeIds={reviewScope}
          onOpenCrop={setCropId}
          onClose={() => {
            setReviewOpen(false);
            setReviewScope(null);
          }}
        />
      )}

      {/* Owner 2026-07-27: REVIEW is the priority action — these prints were
          deliberately added, so continuing past them silently drops intended
          sales. Discarding is a plain text link behind a confirm. */}
      {attnOpen && attention && (
        <ModalShell
          onClose={() => setAttnOpen(false)}
          width={460}
          label="Some prints need your attention"
          className="confirm"
        >
          <div className="confirm__body">
            <h3 className="confirm__title">These prints just need a quick review</h3>
            <p className="confirm__text">
              {attention.label}. It only takes a minute, and reviewing keeps them in your order.
            </p>
            <div className="confirm__actions confirm__actions--stack">
              <button
                type="button"
                className="btn btn--primary confirm__cta"
                onClick={() => {
                  setAttnOpen(false);
                  openReview();
                }}
              >
                Review now →
              </button>
              {/* Only when something would be LEFT: discarding every print
                  would land them on an empty cart. */}
              {pricedCount > 0 && prints.length > attention.total && (
                <button
                  type="button"
                  className="confirm__discard"
                  onClick={() => {
                    setAttnOpen(false);
                    setDiscardOpen(true);
                  }}
                >
                  Discard these prints and continue
                </button>
              )}
            </div>
          </div>
        </ModalShell>
      )}

      {discardOpen && attention && (
        <ModalShell
          onClose={() => setDiscardOpen(false)}
          width={440}
          label="Discard the prints that need review"
          className="confirm"
        >
          <div className="confirm__body">
            <h3 className="confirm__title">Are you sure?</h3>
            <p className="confirm__text">
              These prints just need review, it should only take a minute. Discarding leaves{' '}
              {attention.total} print{attention.total === 1 ? '' : 's'}
              {discardFramedCount > 0 &&
                ` (including ${discardFramedCount} framed print${discardFramedCount === 1 ? '' : 's'})`}{' '}
              out of your order.
            </p>
            <div className="confirm__actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => {
                  setDiscardOpen(false);
                  setAttnOpen(false);
                  discardFlaggedAndContinue();
                }}
              >
                Discard &amp; continue
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  setDiscardOpen(false);
                  openReview();
                }}
              >
                Review now →
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {gateOpen && (
        <BorderGateModal
          pricing={pricing}
          catalog={catalog}
          actions={actions}
          onClose={() => setGateOpen(false)}
          onContinue={() => setStep(2)}
        />
      )}

      {frameNudge && (() => {
        const p = prints.find((x) => x.id === frameNudge.printId);
        const offer = p?.paper ? sameSizeFrameOption(pricing, p.sizeId, p.paper) : null;
        if (!p || !offer) return null;
        return (
          <FrameNudgeSheet
            pricing={pricing}
            offer={offer}
            photo={photos.find((ph) => ph.id === frameNudge.photoId)}
            printPrice={p.unitPrice ?? 0}
            onSee={() => {
              reportUpsell('accepted', 'frame_sheet', offer.frameId);
              setFrameNudge(null);
              setFrameDialogFor(frameNudge.printId);
            }}
            onClose={() => {
              // "No thanks" / Esc / backdrop — all count as a dismissal for
              // the first-party funnel (the ignore signal the owner asked for).
              reportUpsell('dismissed', 'frame_sheet', offer.frameId);
              setFrameNudge(null);
            }}
          />
        );
      })()}
      {frameDialogFor && (() => {
        const p = prints.find((x) => x.id === frameDialogFor);
        const group = p
          ? totals?.groups.find((g) => !g.frameId && g.sizeId === p.sizeId && g.paper === p.paper)
          : undefined;
        if (!p || !group) return null;
        return (
          <AddFrameDialog
            group={group}
            members={groupMembers(prints, group)}
            photosById={new Map(photos.map((ph) => [ph.id, ph]))}
            pricing={pricing}
            onClose={() => setFrameDialogFor(null)}
            onReview={(printId) => {
              setFrameDialogFor(null);
              setCropId(printId);
            }}
          />
        );
      })()}
      {cropPrint && cropPhoto && (
        <CropEditor
          key={cropPrint.id}
          print={cropPrint}
          photo={cropPhoto}
          pricing={pricing}
          catalog={catalog}
          actions={actions}
          onSaved={onEdited}
          onClose={() => setCropId(null)}
        />
      )}

      {!isMobile && !guideSeen && <GuideModal onDone={markGuideSeen} />}
      {saveOpen && <SaveOrderModal source="step2" onClose={() => setSaveOpen(false)} />}

      {printlessOpen && (
        <ModalShell
          onClose={backOutPrintless}
          width={440}
          label="Photos without prints"
          className="confirm"
        >
          <div className="confirm__body">
            <h3 className="confirm__title">
              {printlessCount} photo{printlessCount === 1 ? ' has' : 's have'} no prints yet
            </h3>
            <p className="confirm__text">
              {printlessOffer
                ? /* Sales line (owner-picked 2026-08-17): completion instinct,
                     no warning talk — the old "won't be included" read as
                     confusing next to an offer. */
                  'Finish the set. One small print of each, added in a tap.'
                : `${printlessCount === 1 ? 'It' : 'They'} won’t be included in your order. Go back to add sizes, or continue without ${printlessCount === 1 ? 'it' : 'them'}.`}
            </p>
            {printlessOffer && (() => {
              /* The photos this tap will ADD (owner 2026-08-17): thumbnails so
                 there is no mystery about which ones, max 12 + "+N more", and
                 the copy states the exact size, ONE of each, and the price. */
              const items = printlessOffer.items;
              const thumbs = items.slice(0, 12);
              const sizes = [...new Set(items.map((it) => it.sizeId))];
              const each =
                sizes.length === 1
                  ? `One ${sizeShort(catalog.get(sizes[0]!)?.size ?? sizes[0]!)}\u2033 ${printlessPaper} print of each \u00b7 ${formatMoney(items[0]!.price)} each`
                  : 'One small print of each, sized to fit its photo';
              return (
                <>
                  <div className="confirm__thumbs" aria-hidden>
                    {thumbs.map((it) => {
                      const ph = photos.find((x) => x.id === it.photoId);
                      return ph?.previewUrl ? (
                        <img key={it.photoId} className="confirm__thumb" src={ph.previewUrl} alt="" />
                      ) : (
                        <span key={it.photoId} className="confirm__thumb confirm__thumb--ph" />
                      );
                    })}
                    {items.length > thumbs.length && (
                      <span className="confirm__thumbs-more">+{items.length - thumbs.length} more</span>
                    )}
                  </div>
                  <p className="confirm__text confirm__offer-note">{each}</p>
                  <div className="confirm__offer-row">
                  <Segmented
                    variant="light"
                    ariaLabel="Finish"
                    segments={[
                      { key: 'lustre', label: 'Lustre' },
                      { key: 'gloss', label: 'Gloss' },
                    ]}
                    value={printlessPaper}
                    onChange={(key) => setPrintlessPaper(key as 'gloss' | 'lustre')}
                  />
                  <button
                    type="button"
                    className="btn btn--gold confirm__offer"
                    onClick={() => {
                      reportUpsell('accepted', 'noprints_offer', items[0]!.sizeId);
                      for (const it of items) {
                        // ONE edge print each, chosen finish, PERFECT-fit size:
                        // no crop, no border gate, no low-res flag possible.
                        actions.ensurePrint(it.photoId, it.sizeId, printlessPaper, 'none');
                      }
                      setPrintlessOpen(false);
                      continuePastAttention();
                    }}
                  >
                    {items.length === printlessCount
                      ? `Add all ${items.length}`
                      : `Add these ${items.length}`}
                    {' \u00b7 +'}
                    {formatMoney(printlessOffer.total)}
                  </button>
                  </div>
                </>
              );
            })()}
            <div className="confirm__actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={backOutPrintless}
              >
                Go back
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  if (printlessOffer)
                    reportUpsell('dismissed', 'noprints_offer', printlessOffer.items[0]!.sizeId);
                  setPrintlessOpen(false);
                  continuePastAttention();
                }}
              >
                Continue without {printlessCount === 1 ? 'it' : 'them'} →
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* LAST so it stacks above the crop editor (same z-index, DOM order wins). */}
      {paperInfoOpen && (
        <PaperInfoDialog
          pricing={pricing}
          onClose={() => setPaperInfoOpen(false)}
          /* Click-through only while a LOOSE print is being configured — with
             a frame pending, paper changes must go through the frame's
             sibling list (they change the framed product), so the dialog
             falls back to reference-only rather than half-working. */
          select={
            spPendingFrameId
              ? undefined
              : {
                  sizeId: applyBar.sizeId,
                  paper: applyBar.paper,
                  onPick: (paper) => setApplyBar({ paper }),
                }
          }
        />
      )}
    </Chrome>
  );
}
